import { Button } from 'react-bootstrap';
import { TiLocationOutline } from 'react-icons/ti';
import { FiAlertTriangle } from 'react-icons/fi';
import { GrLocation } from 'react-icons/gr';
import { format, isBefore } from 'date-fns';
import { QueryClient } from '@tanstack/react-query';
import { UserActionAssociatedTypeEnum } from '@company/types/comm-hub.type';
import { isUpcomingActivity } from '@services/activity-tracker/activityTrackerService';
import { isShiftConfirmed } from '@services/shift/shiftService';
import { ActivityDetail } from '@th-types/daily-activity.type';
import IModalConfirmModel from '@th-types/confirm-model.type';
import IJobDetail, { ShiftPosition } from '@th-types/job.detail.type';
import Address from '@th-types/address.type';
import {
  IJobStats,
  ShiftStatus,
  IJobListFilters,
  JobQuickFilters,
  JobShiftsQuickFilters,
  IJobNewApplicantsCounter,
  IJobShiftsResult,
  IJobShiftsCounters,
} from '@th-types/jobs.type';
import DATE_FORMATS from '@constants/dateFormat';
import {
  convertToTimezone,
  formatLocalDate,
  getDateNowByTimezone,
  UTC_TIMEZONE,
} from './DateUtils';

export enum ActivityTrackerStatus {
  OPEN = 'Open',
  PENDING = 'Pending',
  IN_PROGRESS = 'In Progress',
  TO_START = 'To Start',
  MISSING_HOURS = 'Missing Hours',
  NO_CHECK_IN_OUT = 'No Check In/Out',
  TO_APPROVE = 'To Approve',
  APPROVED = 'Approved',
}

export const jobFilterMap: Record<JobQuickFilters, keyof IJobListFilters> = {
  [JobQuickFilters.NEW_APPLICANTS]: 'newApplicantsOnly',
  [JobQuickFilters.HIDDEN_JOBS]: 'hiddenJobs',
};

export const jobShiftsFilterMap: Record<
  JobShiftsQuickFilters,
  keyof IJobListFilters
> = {
  [JobShiftsQuickFilters.CURRENT_OPEN_SHIFTS]: 'openShifts',
  [JobShiftsQuickFilters.MARK_PAID]: 'markPaid',
  [JobShiftsQuickFilters.NO_CHECK_IN_OUT]: 'noCheckInOut',
  [JobShiftsQuickFilters.MISSING_HOURS]: 'missingHours',
  [JobShiftsQuickFilters.ONBOARDING]: 'onboarding',
  [JobShiftsQuickFilters.TO_APPROVE]: 'toApprove',
  [JobShiftsQuickFilters.UPCOMING]: 'upcoming',
};

function getNewApplicantsToDismiss(jobIds: number[]) {
  return {
    items: [
      {
        associatedType: UserActionAssociatedTypeEnum.JOB_APPLICATIONS,
        associatedIds: jobIds,
      },
    ],
  };
}

function getUpdatedNewApplicantsCounter(
  filteredNewApplicants: IJobNewApplicantsCounter[]
) {
  // get the new total value of new applicants
  return filteredNewApplicants.reduce(
    (accumulator, currentValue) =>
      accumulator + currentValue.totalNewApplicants,
    0
  );
}

const getJobFormattedLocalStartDate = (
  job: IJobDetail,
  dateFormat: string = DATE_FORMATS.SHORT_DATE_FORMAT
): string => {
  try {
    return formatLocalDate(job.jobStart, dateFormat, job.timezone);
  } catch (formatError) {
    console.error('Cannot parse job start date', formatError);
    return 'cannot parse';
  }
};

export const SMALL_ICONS_SIZE = 16;
export const LARGE_ICONS_SIZE = 20;

export const emptyJobStats = (jobId: number): IJobStats => ({
  jobId,
  countReviewsMissed: 0,
  countMissingHours: 0,
  countApproveHours: 0,
  countMarkPaid: 0,
  countOnboardingPending: 0,
  countAvailablePositions: 0,
  countTakenPositions: 0,
  countHiredWorkers: 0,
  countPendingWorkers: 0,
  countHiddenWorkers: 0,
  countTotalShifts: 0,
  countCheckIns: 0,
  countCheckOuts: 0,
  countPendingCheckIns: 0,
  countPendingCheckOuts: 0,
  countNewApplicants: 0,
  countSuggestedWorkers: 0,
  countConsideredWorkers: 0,
});

export const getTotalApplicants = (jobStats?: IJobStats) => {
  return jobStats?.countPendingWorkers ?? 0;
};

export const getTotalHiddenApplicants = (jobStats?: IJobStats) => {
  return jobStats?.countHiddenWorkers ?? 0;
};

export const getFilledPosition = (job: IJobDetail, jobStats: IJobStats) => {
  return `${jobStats.countHiredWorkers}/${job.totalPositions}`;
};

export const isJobHidden = (job: IJobDetail, companyMemberId: number) => {
  return job.members.some(
    (member) => member.id === companyMemberId && member.hasJobHidden
  );
};

export const getGoogleMapsUrl = (address: string) =>
  `https://www.google.com/maps?q=${encodeURIComponent(address)}`;

export const getAddressLink = (nationalJob: boolean, address: Address) => {
  if (nationalJob) {
    return <div className="text-capitalize address-text">National Job</div>;
  }
  return (
    <a
      className="address-btn item-links text-capitalize cursor-point address-text"
      href={getGoogleMapsUrl(address?.googleAddress || '')}
      target="_blank"
      rel="noopener noreferrer"
    >
      <TiLocationOutline
        className="mx-0 mb-1 ms-0 me-1"
        size={LARGE_ICONS_SIZE}
      />
      {address?.googleAddress}
    </a>
  );
};

export const isActivityToApprove = (item: ActivityDetail) => {
  return item.timeSheet && !item.timeSheet.isApproved;
};

export const getJobShiftActivityStatus = (
  job: IJobDetail,
  activity: ActivityDetail
) => {
  if (activity.shiftPosition.shiftStatus === ShiftStatus.OPEN) {
    return ActivityTrackerStatus.OPEN;
  }

  if (activity.shiftPosition.shiftStatus === ShiftStatus.PENDING) {
    return ActivityTrackerStatus.PENDING;
  }

  // Fallback to UTC if address or timezoneId is missing
  const timezoneId = job?.address?.timezoneId || UTC_TIMEZONE;

  const dateNowOnTz = convertToTimezone(new Date(), timezoneId);
  const startDate = convertToTimezone(activity.shiftPosition.start, timezoneId);
  const endDate = convertToTimezone(activity.shiftPosition.end, timezoneId);

  if (
    isUpcomingActivity(activity, timezoneId) ||
    isBefore(dateNowOnTz, startDate)
  ) {
    return ActivityTrackerStatus.TO_START;
  }
  if (isBefore(startDate, dateNowOnTz) && !isBefore(endDate, dateNowOnTz)) {
    return ActivityTrackerStatus.IN_PROGRESS;
  }
  if (!activity.timeSheet) {
    return ActivityTrackerStatus.MISSING_HOURS;
  }
  if (
    isShiftConfirmed(activity.shiftPosition) &&
    !isUpcomingActivity(activity, timezoneId) &&
    isActivityToApprove(activity)
  ) {
    return ActivityTrackerStatus.TO_APPROVE;
  }
  return ActivityTrackerStatus.APPROVED;
};

export const getStatusColumn = (
  job: IJobDetail,
  activity: ActivityDetail,
  handleMissingHourNoticed: (shiftPositionId: number) => void
) => {
  const activityStatus = getJobShiftActivityStatus(job, activity);
  return (
    <td
      className={`${activityStatus === ActivityTrackerStatus.MISSING_HOURS ? 'cursor-point' : ''} at-status-colors at-status-colors__${activityStatus
        .replaceAll(' ', '_')
        .replaceAll('/', '')
        .toLowerCase()}${activity.shiftPosition.noticedMissingHours ? '__noticed' : ''}`}
      onClick={() => {
        if (activityStatus === ActivityTrackerStatus.MISSING_HOURS) {
          handleMissingHourNoticed(activity.shiftPosition.id);
        }
      }}
    >
      {activityStatus}
    </td>
  );
};

const getMissingActivityBlock = (
  shiftPosition: ShiftPosition,
  handleMissingActivityNoticed?: (shiftPositionId: number) => void
) => {
  return (
    <div
      className={`${!handleMissingActivityNoticed ? '' : 'cursor-point'}`}
      onClick={() => {
        if (handleMissingActivityNoticed) {
          handleMissingActivityNoticed(shiftPosition.id);
        }
      }}
    >
      <FiAlertTriangle
        color={
          shiftPosition.noticedMissingActivities ? 'var(--black)' : 'var(--red)'
        }
        className="mb-1 me-1"
        size={16}
      />
      <span
        className={`ps-1 missing-timesheet${handleMissingActivityNoticed && shiftPosition.noticedMissingActivities ? '__noticed' : ''}`}
      >
        Missing
      </span>
    </div>
  );
};

const getCheckInValue = (
  job: IJobDetail,
  activity: ActivityDetail,
  handleOpenReviewShiftModal?: (activity: ActivityDetail) => void,
  handleMissingActivityNoticed?: (shiftPositionId: number) => void
) => {
  if (!job || !job.timezone) {
    return null;
  }
  if (activity.shiftPosition.shiftStatus !== ShiftStatus.CONFIRMED) {
    return <div> - </div>;
  }
  if (activity.dailyActivity?.localizedCheckInTime) {
    const date = new Date(activity.dailyActivity.localizedCheckInTime);
    if (handleOpenReviewShiftModal) {
      return (
        <Button
          variant="link"
          onClick={() => handleOpenReviewShiftModal(activity)}
        >
          <GrLocation className="mb-1 me-1" />{' '}
          {format(date, DATE_FORMATS.TIME_FORMAT)}
        </Button>
      );
    }
    return (
      <>
        <GrLocation className="mb-1 me-1" />{' '}
        {format(date, DATE_FORMATS.TIME_FORMAT)}
      </>
    );
  }
  if (
    convertToTimezone(
      new Date(activity.shiftPosition.start),
      job?.timezone || UTC_TIMEZONE
    ) > getDateNowByTimezone(job?.timezone)
  ) {
    return <span className="fst-italic">Upcoming</span>;
  }
  return getMissingActivityBlock(
    activity.shiftPosition,
    handleMissingActivityNoticed
  );
};

const getCheckOutValue = (
  job: IJobDetail,
  activity: ActivityDetail,
  handleOpenReviewShiftModal?: (activity: ActivityDetail) => void,
  handleMissingActivityNoticed?: (shiftPositionId: number) => void
) => {
  if (!job || !job.timezone) {
    return null;
  }
  if (activity.shiftPosition.shiftStatus !== ShiftStatus.CONFIRMED) {
    return <div> - </div>;
  }
  if (activity.dailyActivity?.localizedCheckOutTime) {
    const date = new Date(activity.dailyActivity.localizedCheckOutTime);
    if (handleOpenReviewShiftModal) {
      return (
        <Button
          variant="link"
          onClick={() => handleOpenReviewShiftModal(activity)}
        >
          <GrLocation className="mb-1 me-1" />
          {format(date, DATE_FORMATS.TIME_FORMAT)}
        </Button>
      );
    }
    return (
      <>
        <GrLocation className="mb-1 me-1" />{' '}
        {format(date, DATE_FORMATS.TIME_FORMAT)}
      </>
    );
  }
  const startDate = convertToTimezone(
    new Date(activity.shiftPosition.start),
    job.timezone
  );
  const endDate = convertToTimezone(
    new Date(activity.shiftPosition.end),
    job.timezone
  );
  if (startDate > getDateNowByTimezone(job?.timezone)) {
    return <span className="fst-italic">Upcoming</span>;
  }
  const isJobInProgress =
    startDate <= getDateNowByTimezone(job?.timezone) &&
    endDate > getDateNowByTimezone(job?.timezone);
  if (isJobInProgress) {
    return <span className="fst-italic">In progress</span>;
  }

  return getMissingActivityBlock(
    activity.shiftPosition,
    handleMissingActivityNoticed
  );
};

const getMarkPaidModalConfirmData = (
  activity: ActivityDetail,
  showConfirmModal: boolean,
  setModalConfirmModel: (modalConfirmModel: IModalConfirmModel) => void,
  handleMarkPaid: (activity: ActivityDetail) => void
) => {
  const { isPaid } = activity.shiftPosition;
  setModalConfirmModel({
    message: (
      <div className="text-center">
        <p>
          This action will mark{' '}
          <b>this shift as {isPaid ? 'unpaid' : 'paid'}</b>
        </p>
      </div>
    ),
    titleMessage: (
      <div>
        <span>Are you sure?</span>
      </div>
    ),
    confirmFunction: () => handleMarkPaid(activity),
    show: showConfirmModal,
    styleClass: 'modal-confirm-style',
  });
};

const submitHoursRefreshCounterQueryData = (
  queryJobShiftsCounters: string[],
  queryClient: QueryClient,
  isShiftPaid: boolean
) => {
  queryClient.setQueryData(
    queryJobShiftsCounters,
    (currentCounters: IJobShiftsCounters) => {
      return {
        ...currentCounters,
        countMissingHours: currentCounters.countMissingHours - 1,
        countToApprove: currentCounters.countToApprove + 1,
        countMarkPaid: isShiftPaid
          ? currentCounters.countMarkPaid
          : currentCounters.countMarkPaid + 1,
      };
    }
  );
};

const submitHoursRefreshQueryData = (
  currentActivity: ActivityDetail,
  queryJobShiftsList: string[],
  queryClient: QueryClient,
  jobShiftsListResult: IJobShiftsResult
) => {
  const jobShiftsList = jobShiftsListResult.jobShifts;

  const refreshedList = jobShiftsList.map((jobShiftsItem) => {
    if (
      !jobShiftsItem.jobShiftActivities.every(
        (activityItem) =>
          activityItem.shiftPosition.id !== currentActivity.shiftPosition.id
      )
    ) {
      const activitiesList = jobShiftsItem.jobShiftActivities.filter(
        (item) => item.shiftPosition.id !== currentActivity.shiftPosition.id
      );

      return { ...jobShiftsItem, jobShiftActivities: activitiesList };
    }
    return jobShiftsItem;
  });

  queryClient.setQueryData(queryJobShiftsList, {
    ...jobShiftsListResult,
    jobShifts: refreshedList,
  });
};

const approveTimeSheetRefreshQueryData = (
  approvedBy: string,
  currentActivity: ActivityDetail,
  queryJobShiftsList: string[],
  queryClient: QueryClient,
  jobShiftsListResult: IJobShiftsResult
) => {
  const jobShiftsList = jobShiftsListResult.jobShifts;

  const refreshedList = jobShiftsList.map((jobShiftsItem) => {
    if (
      !jobShiftsItem.jobShiftActivities.every(
        (activityItem) =>
          activityItem.shiftPosition.id !== currentActivity.shiftPosition.id
      )
    ) {
      const activitiesList = jobShiftsItem.jobShiftActivities.map(
        (activityItem) => {
          if (
            activityItem.shiftPosition.id ===
              currentActivity.shiftPosition.id &&
            activityItem.timeSheet
          ) {
            return {
              ...activityItem,
              timeSheet: {
                ...activityItem.timeSheet,
                approvedBy,
                isApproved: true,
              },
            };
          }
          return activityItem;
        }
      );

      return { ...jobShiftsItem, jobShiftActivities: activitiesList };
    }
    return jobShiftsItem;
  });

  queryClient.setQueryData(queryJobShiftsList, {
    ...jobShiftsListResult,
    jobShifts: refreshedList,
  });
};

const markPaidRefreshCounterQueryData = (
  queryJobShiftsCounters: string[],
  queryClient: QueryClient,
  isShiftPaid: boolean
) => {
  queryClient.setQueryData(
    queryJobShiftsCounters,
    (currentCounters: IJobShiftsCounters) => {
      return {
        ...currentCounters,
        countMarkPaid: isShiftPaid
          ? currentCounters.countMarkPaid + 1
          : currentCounters.countMarkPaid - 1,
      };
    }
  );
};

const markPaidSuccessRefreshQueryData = (
  currentActivity: ActivityDetail,
  queryJobShiftsList: string[],
  queryClient: QueryClient,
  jobShiftsListResult: IJobShiftsResult
) => {
  const targetShiftId = currentActivity.shiftPosition.id;

  queryClient.setQueryData(queryJobShiftsList, {
    ...jobShiftsListResult,
    jobShifts: jobShiftsListResult.jobShifts.map((jobShiftsItem) => {
      // Check if this jobShiftsItem contains the target activity
      const hasTargetActivity = jobShiftsItem.jobShiftActivities.some(
        (activity) => activity.shiftPosition.id === targetShiftId
      );

      if (!hasTargetActivity) {
        return jobShiftsItem;
      }

      return {
        ...jobShiftsItem,
        jobShiftActivities: jobShiftsItem.jobShiftActivities.map(
          (activityItem) => {
            if (activityItem.shiftPosition.id === targetShiftId) {
              return {
                ...activityItem,
                shiftPosition: {
                  ...activityItem.shiftPosition,
                  isPaid: !activityItem.shiftPosition.isPaid,
                },
              };
            }
            return activityItem;
          }
        ),
      };
    }),
  });
};

const cancelShiftSuccessRefreshQueryData = (
  currentActivity: ActivityDetail,
  queryJobShiftsList: string[],
  queryClient: QueryClient,
  jobShiftsListResult: IJobShiftsResult
) => {
  const refreshedList = jobShiftsListResult.jobShifts.map((jobShiftsItem) => {
    if (
      !jobShiftsItem.jobShiftActivities.every(
        (activityItem) =>
          activityItem.shiftPosition.id !== currentActivity.shiftPosition.id
      )
    ) {
      const activitiesList = jobShiftsItem.jobShiftActivities.map(
        (activityItem) => {
          if (
            activityItem.shiftPosition.id === currentActivity.shiftPosition.id
          ) {
            return {
              ...activityItem,
              shiftPosition: {
                ...activityItem.shiftPosition,
                shiftStatus: ShiftStatus.OPEN,
                jobAssignment: undefined,
              },
              dailyActivity: undefined,
              timeSheet: undefined,
            };
          }
          return activityItem;
        }
      );
      return { ...jobShiftsItem, jobShiftActivities: activitiesList };
    }
    return jobShiftsItem;
  });

  queryClient.setQueryData(queryJobShiftsList, {
    ...jobShiftsListResult,
    jobShifts: refreshedList,
  });
};

export {
  getCheckInValue,
  getCheckOutValue,
  getNewApplicantsToDismiss,
  submitHoursRefreshQueryData,
  getMarkPaidModalConfirmData,
  getJobFormattedLocalStartDate,
  getUpdatedNewApplicantsCounter,
  markPaidSuccessRefreshQueryData,
  markPaidRefreshCounterQueryData,
  approveTimeSheetRefreshQueryData,
  submitHoursRefreshCounterQueryData,
  cancelShiftSuccessRefreshQueryData,
};
